import type { TeamSelectorValue } from '@kizen/api';
import type { EntityRecordDetail } from '@kizen/api/records';
import {
  isOwnerValue,
  isPercentageChanceToCloseValue,
  isStageValue,
} from '@kizen/checks/field-value';
import { monitoringMessageHelper } from 'instrumentation/helpers';

type FieldLike = {
  id: string;
  name: string;
};

const toTeamMemberString = (
  tm: Exclude<TeamSelectorValue['value'], null>
): string => {
  if (tm.email) {
    return `${tm.first_name} ${tm.last_name} (${tm.email})`;
  }

  return `${tm.first_name} ${tm.last_name}`;
};

export const getFieldInputValue = (
  field: FieldLike,
  entityRecord: EntityRecordDetail
) => {
  if (!field) {
    return;
  }

  const fv = entityRecord.fields[field.id];

  if (!fv) {
    return;
  }

  if (isStageValue(fv)) {
    if (!fv.value) {
      monitoringMessageHelper(
        'Found an entity record without a stage value - this is unexpected',
        { extra: { entity_id: entityRecord.id } }
      );

      return;
    }

    return fv.value.id;
  }

  if (isOwnerValue(fv)) {
    if (!fv.value) {
      return;
    }

    return {
      id: fv.value.id,
      label: toTeamMemberString(fv.value),
    };
  }

  if (isPercentageChanceToCloseValue(fv)) {
    return fv.value ?? fv.stage_value;
  }

  switch (fv.field_type) {
    case 'checkboxes':
    case 'dynamictags':
      return fv.value.map((opt) => {
        return { value: opt.id, label: opt.name };
      });
    case 'checkbox':
    case 'date':
    case 'datetime':
    case 'decimal':
    case 'email':
    case 'files':
    case 'integer':
    case 'longtext':
    case 'rating':
    case 'text':
      return fv.value;
    case 'dropdown':
    case 'radio':
    case 'status':
    case 'yesnomaybe':
      return fv.value.id;
    case 'phonenumber':
      return fv.value?.e164;
    case 'money':
      return fv.value?.amount;

    case 'relationship': {
      if ('value_summary' in fv) {
        return fv.value_summary;
      }

      if (Array.isArray(fv.value)) {
        return fv.value.map((opt) => {
          if ('display_name' in opt) {
            return { id: opt.id, label: opt.display_name || opt.name };
          }
          return {
            id: opt.id,
            label: toTeamMemberString(opt),
          };
        });
      }

      if ('display_name' in fv.value) {
        return {
          id: fv.value.id,
          label: fv.value.display_name || fv.value.name,
        };
      }

      return {
        id: fv.value.id,
        label: toTeamMemberString(fv.value),
      };
    }

    case 'timezone':
      return fv.value?.id;

    case 'team_selector':
      return {
        id: fv.value.id,
        label: toTeamMemberString(fv.value),
      };
  }
};
